import { now } from './date'

export enum NativeMediaErrorCode {
  NONE = 0,
  ABORT = 1,
  NETWORK = 2,
  DECODE = 3,
  NO_SUPPORT = 4,
}

export enum NativeMediaEventType {
  LOADED_DATA = 'loadeddata',
  PLAY = 'play',
  PAUSE = 'pause',
  TIME_UPDATE = 'timeupdate',
  ERROR = 'error',
  ENDED = 'ended',
  SEEKING = 'seeking',
  LOAD_START = 'loadstart',
  LOADED_METADATA = 'loadedmetadata',
  CAN_PLAY = 'canplay',
  PLAYING = 'playing',
  WAITING = 'waiting',
  SEEKED = 'seeked',
  RATE_CHANGE = 'ratechange',
  DURATION_CHANGE = 'durationchange',
  EMPTIED = 'emptied',
}

enum NativeWindowEventType {
  PAGE_HIDE = 'pagehide',
}

enum NativeDocumentEventType {
  VISIBILITY_CHANGE = 'visibilitychange',
}

export enum ObserverMediaEventType {
  LOAD_START = 1,
  DURATION_CHANGE = 2,
  LOADED_METADATA = 3,
  LOADED_DATA = 4,
  CAN_PLAY = 5,
  PLAY = 6,
  PAUSE = 7,
  PLAYING = 8,
  WAITING = 9,
  TIME_UPDATE = 10,
  SEEKING = 11,
  SEEKED = 12,
  RATE_CHANGE = 13,
  ENDED = 14,
  EMPTIED = 15,
  SESSION_CLOSED = 16,
  ERROR = 17,
  VISIBILITY_CHANGE = 18,
}

export type ObserverMediaEvent = {
  emittedAt: number
  type: ObserverMediaEventType
  currentTime: number
  videoWidth: number
  videoHeight: number
  paused: boolean
  playbackRate: number
  errorCode: NativeMediaErrorCode
}

// A map between native and observer media events to translate native types.
// NOTE: Not generated dynamically to limit mistakes when adding new mappings.
const eventTypeMap: any = {}
eventTypeMap[NativeMediaEventType.LOADED_DATA] =
  ObserverMediaEventType.LOADED_DATA
eventTypeMap[NativeMediaEventType.PLAY] = ObserverMediaEventType.PLAY
eventTypeMap[NativeMediaEventType.PAUSE] = ObserverMediaEventType.PAUSE
eventTypeMap[NativeMediaEventType.TIME_UPDATE] =
  ObserverMediaEventType.TIME_UPDATE
eventTypeMap[NativeMediaEventType.ERROR] = ObserverMediaEventType.ERROR
eventTypeMap[NativeMediaEventType.ENDED] = ObserverMediaEventType.ENDED
eventTypeMap[NativeMediaEventType.SEEKING] = ObserverMediaEventType.SEEKING
eventTypeMap[NativeMediaEventType.LOAD_START] =
  ObserverMediaEventType.LOAD_START
eventTypeMap[NativeMediaEventType.LOADED_METADATA] =
  ObserverMediaEventType.LOADED_METADATA
eventTypeMap[NativeMediaEventType.CAN_PLAY] = ObserverMediaEventType.CAN_PLAY
eventTypeMap[NativeMediaEventType.PLAYING] = ObserverMediaEventType.PLAYING
eventTypeMap[NativeMediaEventType.WAITING] = ObserverMediaEventType.WAITING
eventTypeMap[NativeMediaEventType.SEEKED] = ObserverMediaEventType.SEEKED
eventTypeMap[NativeMediaEventType.RATE_CHANGE] =
  ObserverMediaEventType.RATE_CHANGE
eventTypeMap[NativeMediaEventType.DURATION_CHANGE] =
  ObserverMediaEventType.DURATION_CHANGE
eventTypeMap[NativeMediaEventType.EMPTIED] = ObserverMediaEventType.EMPTIED

export function observeMedia(
  media: HTMLVideoElement,
  cb: (args: ObserverMediaEvent) => void,
) {
  const emit = (observerEventType: ObserverMediaEventType) => {
    cb({
      emittedAt: now(),
      type: observerEventType,
      currentTime: media.currentTime,
      videoWidth: media.videoWidth,
      videoHeight: media.videoHeight,
      paused: media.paused,
      playbackRate: media.playbackRate,
      errorCode: media.error ? media.error.code : NativeMediaErrorCode.NONE,
    })
  }

  const onNativeMediaEvent = (event: Event) => {
    const observerEventType = eventTypeMap[event.type] || undefined

    if (!observerEventType) {
      return
    }

    emit(observerEventType)
  }

  const nativeEventTypes = Object.values(NativeMediaEventType)

  // Add event listeners on all entries listed in NativeMediaEventType.
  nativeEventTypes.forEach((nativeEventType) => {
    media.addEventListener(nativeEventType, onNativeMediaEvent)
  })

  const onNativeWindowPageHideEvent = () => {
    emit(ObserverMediaEventType.SESSION_CLOSED)
  }

  // Add event listeners for NativeWindowEventType
  window.addEventListener(
    NativeWindowEventType.PAGE_HIDE,
    onNativeWindowPageHideEvent,
  )

  const onNativeDocumentVisibilityChangeEvent = () => {
    emit(ObserverMediaEventType.VISIBILITY_CHANGE)
  }

  // Add event listeners for NativeDocumentEventType
  document.addEventListener(
    NativeDocumentEventType.VISIBILITY_CHANGE,
    onNativeDocumentVisibilityChangeEvent,
  )

  return () => {
    // Remove event listeners on all entries listed in NativeMediaEventType.
    nativeEventTypes.forEach((nativeEventType) => {
      media.removeEventListener(nativeEventType, onNativeMediaEvent)
    })

    // Remove event listeners for NativeWindowEventType
    window.removeEventListener(
      NativeWindowEventType.PAGE_HIDE,
      onNativeWindowPageHideEvent,
    )

    // Remove event listeners for NativeDocumentEventType
    document.removeEventListener(
      NativeDocumentEventType.VISIBILITY_CHANGE,
      onNativeDocumentVisibilityChangeEvent,
    )
  }
}
